import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Heart, Brain, Pencil } from "lucide-react";

/**
 * Sticky biorhythm banner at the top of your guidance page.
 *
 * Only renders when a DOB is saved in localStorage (`ep_biorhythm_dob`).
 * Uses the same three-cycle math as BiorhythmPanel — physical (23d),
 * emotional (28d), intellectual (33d) — but as a slim horizontal strip.
 */
const BIO_KEY = "ep_biorhythm_dob";
const CYCLES = [
  { name: "Physical",     days: 23, colour: "#e5a87c", Icon: Activity, testid: "banner-bio-physical" },
  { name: "Emotional",    days: 28, colour: "#d4a24c", Icon: Heart,    testid: "banner-bio-emotional" },
  { name: "Intellectual", days: 33, colour: "#c5b6e7", Icon: Brain,    testid: "banner-bio-intellectual" },
];

function daysSinceUTC(dobIso) {
  const dob = new Date(dobIso + "T00:00:00Z");
  const now = new Date();
  const nowUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return Math.floor((nowUTC - dob) / 86400000);
}

function reading(pct) {
  const abs = Math.abs(pct);
  if (abs < 10) return "critical — be gentle";
  if (pct > 75) return "peak — ride the wave";
  if (pct > 30) return "on the rise";
  if (pct > -10) return "steady";
  if (pct > -50) return "resting";
  return "deep dip";
}

export default function BiorhythmBanner() {
  const [dob, setDob] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = localStorage.getItem(BIO_KEY);
    if (saved) setDob(saved);
    // Listen for cross-component updates
    const onStorage = (e) => {
      if (e.key === BIO_KEY) setDob(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const stats = useMemo(() => {
    if (!dob) return null;
    const days = daysSinceUTC(dob);
    return CYCLES.map((c) => ({
      ...c,
      pct: Math.round(Math.sin((2 * Math.PI * days) / c.days) * 100),
    }));
  }, [dob]);

  if (!dob || !stats) return null;

  // Overall vibe: lowest cycle drives the summary
  const lowest = stats.reduce((acc, c) => (c.pct < acc.pct ? c : acc), stats[0]);
  const summary =
    Math.abs(lowest.pct) < 10
      ? `A critical ${lowest.name.toLowerCase()} day — be gentle with yourself.`
      : lowest.pct < -50
        ? `Your ${lowest.name.toLowerCase()} cycle is dipping — rest, hydrate, don't push.`
        : lowest.pct < -10
          ? `Your ${lowest.name.toLowerCase()} cycle is a little low — soft-pace the day.`
          : stats.every((c) => c.pct > 30)
            ? "All three cycles are up — a rare and generous day."
            : "A gentle, steady day across your three cycles.";

  return (
    <div
      data-testid="biorhythm-banner"
      className="border-b border-[color:var(--gold)]/15 backdrop-blur-md"
      style={{ background: "linear-gradient(180deg, rgba(10,17,40,0.85), rgba(10,17,40,0.6))" }}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-3 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
        <div className="flex items-center gap-3 min-w-0">
          <span className="label-overline text-[color:var(--gold)]/80 flex-shrink-0">
            Today · biorhythm
          </span>
          <span
            className="font-serif-italic text-[color:var(--parchment)] text-sm md:text-base truncate"
            data-testid="banner-bio-summary"
          >
            {summary}
          </span>
        </div>
        <div className="flex items-center gap-3 md:gap-5 md:ml-auto">
          {stats.map((c) => (
            <div
              key={c.name}
              className="flex items-center gap-2"
              data-testid={c.testid}
            >
              <c.Icon size={12} style={{ color: c.colour }} />
              <span className="label-overline text-[10px] hidden md:inline">{c.name}</span>
              <span
                className="font-serif-display text-base md:text-lg leading-none"
                style={{ color: c.pct >= 0 ? c.colour : "var(--parchment-soft)" }}
              >
                {c.pct > 0 ? "+" : ""}{c.pct}
              </span>
              <span className="font-serif-italic text-[color:var(--text-muted)] text-[10px] md:text-xs hidden lg:inline">
                {reading(c.pct)}
              </span>
            </div>
          ))}
          <Link
            to="/almanac?meditation=recommend"
            data-testid="banner-bio-meditate"
            className="ml-2 hidden md:inline-flex items-center gap-1 label-overline text-[color:var(--gold)] hover:text-[color:var(--parchment)] transition"
          >
            <Pencil size={11} /> meditate
          </Link>
        </div>
      </div>
    </div>
  );
}
